// External
import React, { useMemo, useCallback, memo } from "react";
import _ from "lodash";
import { Trans } from "react-i18next";

// DS
import { Modal, Button, Alert, Table } from "@toddle-edu/ds-web";
import { UploadOutlined, DownloadOutlined } from "@toddle-edu/ds-icons";

// Hooks
import { useI18n } from "Hooks";

// Module
import { useAcademicRecordsModal } from "../../../../context";
import {
  BULK_UPLOAD_MODAL_TYPES,
  MODAL_WIDTH,
} from "../../modules/BulkUploadConstants";
import {
  getErrorTitleReason,
  getErrorAlertMessage,
  getColumnErrorList,
  hasColumnErrors,
  handleDownloadErrorList,
} from "../../modules/BulkUploadHelpers";

// Styles
import classes from "./BulkUploadErrorModal.scss";

const TEST_ID_PREFIX = "bulk-upload-error-modal";

const BulkUploadErrorModal = () => {
  const { t } = useI18n();
  const {
    modalInfo: { metadata = {} } = {},
    openModal,
    closeModal,
  } = useAcademicRecordsModal();

  const { sheetType, studentIds, studentName, error } = metadata;
  const errorCode = _.get(error, "errorCode");

  const titleReason = getErrorTitleReason(errorCode, error, t);
  const alertMessage = getErrorAlertMessage(errorCode, t);

  const showColumnErrors = hasColumnErrors(error);

  const columnErrorList = useMemo(
    () => (showColumnErrors ? getColumnErrorList(error, t) : []),
    [showColumnErrors, error, t]
  );

  const errorTableHeaders = useMemo(
    () => [
      {
        key: "message",
        value: t("common:error_description"),
      },
    ],
    [t]
  );

  const errorTableData = useMemo(
    () =>
      _.map(columnErrorList, item => ({
        id: item.id,
        rowData: [{ key: "message", value: item.message }],
      })),
    [columnErrorList]
  );

  const handleBackToUpload = useCallback(() => {
    openModal(BULK_UPLOAD_MODAL_TYPES.BULK_UPLOAD_IMPORT, {
      sheetType,
      studentIds,
      studentName,
    });
  }, [openModal, sheetType, studentIds, studentName]);

  const downloadErrorList = useCallback(async () => {
    await handleDownloadErrorList({ columnErrorList, t });
  }, [columnErrorList, t]);

  return (
    <Modal
      isOpen={true}
      height="min-content"
      width={MODAL_WIDTH.ERROR}
      onClose={closeModal}
      shouldCloseOnOverlayClick={false}
    >
      <Modal.Header isClosable={true}>
        <div className={classes.headerContent}>
          <h6>
            <Trans
              i18nKey="transcript:upload_failed_with_styled_reason"
              values={{ reason: titleReason }}
              components={{
                1: <span className={classes.errorReason} />,
              }}
            />
          </h6>
          <p className="text-body text-secondary">
            {showColumnErrors
              ? t("transcript:please_fix_errors_and_reupload")
              : t("transcript:please_fix_error_and_reupload")}
          </p>
        </div>
      </Modal.Header>
      <Modal.Body isScrollable={true}>
        <div className={classes.errorModalBody}>
          {alertMessage && (
            <Alert
              message={alertMessage}
              type="error"
              testId={`${TEST_ID_PREFIX}-alert`}
            />
          )}
          {showColumnErrors && (
            <Table
              dsVersion="2.0"
              headerTheme="light"
              showBorder={false}
              headers={errorTableHeaders}
              data={errorTableData}
              testId={`${TEST_ID_PREFIX}-errors-table`}
              showRowBackgroundOnHover
              isHeaderFixed
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.modalFooter}>
          {showColumnErrors ? (
            <Button
              icon={<DownloadOutlined />}
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={downloadErrorList}
              testId={`${TEST_ID_PREFIX}-download-error-list`}
            >
              {t("common:download_error_list")}
            </Button>
          ) : (
            <Button
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={closeModal}
              testId={`${TEST_ID_PREFIX}-cancel`}
            >
              {t("common:cancel")}
            </Button>
          )}
          <Button
            icon={<UploadOutlined />}
            variant="progressive"
            type="fill"
            size="medium"
            onClick={handleBackToUpload}
            testId={`${TEST_ID_PREFIX}-upload-new-file`}
          >
            {t("common:upload_a_new_file")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(BulkUploadErrorModal);
